import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { keyBy } from 'lodash';
import ReactSelect, { MenuPlacement, MenuPosition } from 'react-select';
import { Box } from '@chakra-ui/react';
import { useTranslation } from 'helpers/languages/translations';

export type TSelectOption = { label: string; value: any };

type TRSelect = {
    options: TSelectOption[];
    onChange: (vals: (string | number)[]) => void;
    selectedOptions: (string | number)[];
    disabled?: boolean;
    setAll?: boolean;
    styles?: Record<string, any>;
    menuPlacement?: MenuPlacement;
    menuPosition?: MenuPosition;
};

export const formatOptionsForRSelect = (options: Record<string, any>[] = [], label: string, value: string) =>
    options.map((option) => ({ label: option[label], value: option[value] }));

const getSelectedById = (selectedOptions: (string | number)[], options: TSelectOption[]) => {
    const optionsById = keyBy(options, 'value');
    return selectedOptions?.map((option) => ({ label: optionsById[option].label, value: optionsById[option].value }));
};

const RSelect = ({
    options: op,
    onChange,
    selectedOptions,
    disabled,
    setAll,
    styles,
    menuPlacement = 'auto',
    menuPosition = 'fixed',
}: TRSelect) => {
    const translate = useTranslation();

    const [value, setValue] = useState<TSelectOption[]>(getSelectedById(selectedOptions, op) || []);
    const [options, setOptions] = useState<TSelectOption[]>(op);
    const isAllSelected = useRef(value.length === options.length);

    const allOption = { label: translate('all') as string, value: 'all' };

    const formatedOptions = useMemo(() => {
        return setAll ? [allOption, ...options] : options;
    }, [setAll, options]);

    const handleChange = useCallback((val: TSelectOption[], noUpdate?: boolean) => {
        const vals = keyBy(val, 'value');

        isAllSelected.current = val.length === options.length;

        setOptions(vals.all ? [] : op);
        setValue(vals.all ? [allOption] : val);
        !noUpdate && onChange((vals.all ? options : val).map((option) => option.value));
    }, []);

    useEffect(() => {
        handleChange(getSelectedById(selectedOptions, op) || [], true);
    }, [selectedOptions]);

    return (
        <Box flexGrow={1} my={2} cursor={disabled ? 'not-allowed' : 'normal'}>
            <ReactSelect
                isMulti
                closeMenuOnSelect={false}
                options={isAllSelected.current ? [allOption] : formatedOptions}
                value={isAllSelected.current ? [allOption] : value}
                onChange={(values) => handleChange(values as any, false)}
                isDisabled={disabled}
                menuPlacement={menuPlacement}
                menuPosition={menuPosition}
                styles={{
                    control: (base) => ({
                        ...base,
                        ...styles,
                    }),
                    valueContainer: (base) => ({
                        ...base,
                        maxHeight: '100px',
                        overflow: 'auto',
                    }),
                }}
            />
        </Box>
    );
};

export default RSelect;
